import { OrderStagesAPI } from '@business/api/order';
import { OrderStageId, OrderStageResponse } from '@business/entities/order';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { map, switchMap } from 'rxjs/operators';

export class OrderStageBloc extends BaseBloc {
  private readonly ordersRepo = new Repository<OrderStageResponse>({
    getItemId: brand => brand.id,
  });

  readonly stages$ = this.ordersRepo.items$.pipe(
    map(items => R.sortBy(items, 'menuOrder')),
    map(items => R.filter(items, i => i.active)),
  );
  readonly all$ = this.ordersRepo.items$.pipe(map(items => R.sortBy(items, 'menuOrder')));

  onReset() {
    this.ordersRepo.reset();
  }

  fetchStages = () => {
    return OrderStagesAPI.fetchStages().pipe(this.ordersRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_order_stages_error') }));
  };

  fetchStage = (id: OrderStageId) => {
    return OrderStagesAPI.fetchStage(id).pipe(
      this.ordersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_order_stages_error') }),
    );
  };

  createStage = (brand: Partial<OrderStageResponse>) => {
    return OrderStagesAPI.createStage(brand).pipe(
      this.ordersRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_order_stage'),
      }),
    );
  };

  updateStage = (id: OrderStageId, brand: Partial<OrderStageResponse>) => {
    return OrderStagesAPI.updateStage(id, brand).pipe(
      this.ordersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_stage'),
      }),
    );
  };

  deleteStage = (stageId: OrderStageId) => {
    return showDeleteConfirmation('Delete Stage', `Do you really want to remove this stage?`).pipe(
      switchMap(() => OrderStagesAPI.deleteStage(stageId)),
      this.ordersRepo.ops.removeOne(() => stageId),
      handleMessage({
        type: requestMessage('delete_order_stage'),
      }),
    );
  };

  updateStatus = (id: OrderStageId) => {
    return OrderStagesAPI.updateStatus(id).pipe(
      this.ordersRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_stage'),
      }),
    );
  };
}
